/**
 * @fileoverview ContactActivityTab — consolidated Notes & Mail view for the
 * contact detail panel (MOR-841).
 *
 * Replaces the separate Notes and Mail tabs with one tab carrying three
 * sub-views:
 *
 * - **All**   — mail messages and file notes interleaved chronologically
 *               (newest first), with a note composer so file notes can be
 *               added without leaving the feed.
 * - **Mail**  — the existing message timeline (`ContactCommsTab`).
 * - **Notes** — the existing notes feed (`ContactNotesFeed`).
 *
 * The AI summary + sentiment card sits above the sub-tabs and is generated
 * from BOTH the correspondence and the file notes (the backend corpus was
 * widened in `convex/mail/commsQueries.ts:getContactCorpus`). For contacts
 * without an email address the combined insight can't be keyed, so the
 * Notes sub-view keeps its notes-only summary card instead.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { formatDistanceToNow } from "date-fns";
import {
  ArrowDownLeft,
  ArrowUpRight,
  FileEdit,
  Loader2,
  Mail,
  RefreshCw,
  Send,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge, Button, Spinner } from "@/ui/primitives";
import {
  NotesEditor,
  type NotesEditorHandle,
} from "@/ui/composed/editor/notes-editor";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";
import { ContactCommsTab } from "@/components/mail/ContactCommsTab";
import { SENTIMENT_META } from "@/components/mail/sentimentMeta";
import type { ContactReader } from "./ContactDetailPanel";
import { ContactNotesFeed } from "./notes/ContactNotesFeed";
import { NoteRow, type FeedNote } from "@/components/contacts/notes/NoteRow";

type ActivitySubTab = "all" | "mail" | "notes";

/** What a feed row is, for filtering. */
type FeedFilterKey = "sent" | "received" | "review" | "drafts" | "notes";

const FEED_FILTERS: ReadonlyArray<{ key: FeedFilterKey; label: string }> = [
  { key: "received", label: "Received" },
  { key: "sent", label: "Sent" },
  { key: "review", label: "In review" },
  { key: "drafts", label: "Drafts" },
  { key: "notes", label: "Notes" },
];

interface MailFeedMessage {
  _id: string;
  threadId: Id<"mailThreads">;
  direction: "inbound" | "outbound";
  subject: string;
  bodyText: string;
  sentAt: number;
}

/** An undecided draft addressed to this contact. */
interface DraftFeedItem {
  draftId: Id<"mailDrafts">;
  subject: string;
  bodyText: string;
  source: string;
  createdAt: number;
  threadId: Id<"mailThreads"> | null;
  agentRationale: string | null;
  revising: boolean;
}

type FeedItem =
  | { kind: "note"; at: number; note: FeedNote }
  | { kind: "mail"; at: number; message: MailFeedMessage }
  | { kind: "draft"; at: number; draft: DraftFeedItem };

interface NotesSummaryFromContact {
  text: string;
  generatedAt: number;
  generatedBy: Id<"users">;
  noteCount: number;
  notesHash: string;
  model: string;
}

/** Which filter bucket a feed row belongs to. */
function feedFilterKey(item: FeedItem): FeedFilterKey {
  if (item.kind === "note") return "notes";
  if (item.kind === "draft") {
    return item.draft.source === "user" ? "drafts" : "review";
  }
  return item.message.direction === "inbound" ? "received" : "sent";
}

function isHtmlEmpty(html: string): boolean {
  const stripped = html
    .replace(/<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>/g, "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
  return stripped.length === 0;
}

export function ContactActivityTab({
  jobId,
  contactId,
  emailAddress,
  readOnly = false,
  currentUserId,
  notesSummary,
  notesCount,
  onOpenChat,
  onOpenThread,
  activeThreadId,
  activeReader,
  onOpenReader,
}: {
  jobId: Id<"jobs">;
  contactId: Id<"jobContacts">;
  emailAddress: string;
  /** Read-only when the parent contact is merged. */
  readOnly?: boolean;
  currentUserId?: Id<"users">;
  /** Cached notes-only AI summary — shown on the Notes sub-view when the
   *  contact has no email (no combined insight possible). */
  notesSummary?: NotesSummaryFromContact;
  notesCount: number;
  onOpenChat: () => void;
  onOpenThread?: (threadId: Id<"mailThreads">) => void;
  activeThreadId?: Id<"mailThreads"> | null;
  /** Whatever the panel's reader pane is showing, for active highlighting. */
  activeReader?: ContactReader;
  /** Opens a draft or a blank compose in the panel's reader pane. */
  onOpenReader?: (reader: ContactReader) => void;
}) {
  const hasEmail = emailAddress.trim().length > 0;
  const [subTab, setSubTab] = useState<ActivitySubTab>("all");

  const { data: notes, isLoading: notesLoading } = useQuery({
    ...convexQuery(api.contacts.notes.listContactNotes, { contactId }),
  });
  const { data: messages, isLoading: mailLoading } = useQuery({
    ...convexQuery(api.mail.commsQueries.listMessagesForContact, {
      jobId,
      emailAddress,
    }),
    enabled: hasEmail,
  });
  // Mail drafted for this contact but not sent — a Morgan draft awaiting
  // approval, or the practitioner's own unsent compose. Both are part of
  // the correspondence with this party, so they belong on their feed
  // rather than only in the mailbox.
  const { data: contactDrafts } = useQuery({
    ...convexQuery(api.mail.contactCompose.listDraftsForContact, {
      jobId,
      emailAddress,
    }),
    enabled: hasEmail,
  });
  const { data: insight } = useQuery({
    ...convexQuery(api.mail.commsQueries.getCommsInsight, {
      jobId,
      emailAddress,
    }),
    enabled: hasEmail,
  });
  const { mutateAsync: requestInsight, isPending: generating } = useMutation({
    mutationFn: useConvexMutation(api.mail.commsQueries.requestCommsInsight),
  });

  const createNote = useConvexMutation(api.contacts.notes.createContactNote);
  const updateNote = useConvexMutation(api.contacts.notes.updateContactNote);
  const deleteNote = useConvexMutation(api.contacts.notes.deleteContactNote);

  // ---- Combined chronological feed ----------------------------------------
  const feedNotes: Array<FeedNote> = useMemo(
    () =>
      (notes ?? []).map((n) => ({
        _id: n._id,
        content: n.content,
        createdBy: n.createdBy,
        createdAt: n.createdAt,
        updatedAt: n.updatedAt,
        authorDisplayName: n.authorDisplayName,
        lastEditedByName: n.lastEditedByName ?? undefined,
      })),
    [notes],
  );

  const feedItems: Array<FeedItem> = useMemo(() => {
    const items: Array<FeedItem> = feedNotes.map((note) => ({
      kind: "note",
      at: note.createdAt,
      note,
    }));
    for (const message of messages ?? []) {
      items.push({ kind: "mail", at: message.sentAt, message });
    }
    for (const draft of contactDrafts ?? []) {
      items.push({ kind: "draft", at: draft.createdAt, draft });
    }
    items.sort((a, b) => b.at - a.at);
    return items;
  }, [feedNotes, messages, contactDrafts]);

  const [activeFilters, setActiveFilters] = useState<Set<FeedFilterKey>>(
    () => new Set(),
  );
  const toggleFilter = useCallback((key: FeedFilterKey) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const feedCounts = useMemo(() => {
    const counts: Record<FeedFilterKey, number> = {
      sent: 0,
      received: 0,
      review: 0,
      drafts: 0,
      notes: 0,
    };
    for (const item of feedItems) counts[feedFilterKey(item)] += 1;
    return counts;
  }, [feedItems]);

  // No filter selected reads as "everything", which is what an empty
  // selection means to a practitioner scanning the feed.
  const visibleItems = useMemo(
    () =>
      activeFilters.size === 0
        ? feedItems
        : feedItems.filter((item) => activeFilters.has(feedFilterKey(item))),
    [feedItems, activeFilters],
  );

  const mailCount = messages?.length ?? 0;

  // ---- Insight staleness (mail OR notes newer than the summary) -----------
  const latestNoteActivity = useMemo(
    () =>
      feedNotes.reduce(
        (latest, n) => Math.max(latest, n.updatedAt ?? n.createdAt),
        0,
      ),
    [feedNotes],
  );
  const stale =
    insight?.status === "complete" &&
    (insight.lastMessageAt > insight.generatedAt ||
      latestNoteActivity > insight.generatedAt);
  const sentimentMeta = insight
    ? (SENTIMENT_META[insight.sentiment] ?? SENTIMENT_META.neutral)
    : null;

  const handleGenerate = async () => {
    try {
      await requestInsight({
        jobId,
        emailAddress,
        jobContactId: contactId,
      });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to generate summary"));
    }
  };

  // ---- Composer (All sub-view) --------------------------------------------
  const [composerDraft, setComposerDraft] = useState("");
  const [isPosting, setIsPosting] = useState(false);
  const composerRef = useRef<NotesEditorHandle | null>(null);

  const handlePost = useCallback(async () => {
    const content = composerDraft;
    if (isHtmlEmpty(content)) {
      toast.info("Write something before posting.");
      return;
    }
    setIsPosting(true);
    try {
      await createNote({ contactId, content });
      setComposerDraft("");
      composerRef.current?.getEditor()?.commands.clearContent();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not post your note"));
    } finally {
      setIsPosting(false);
    }
  }, [composerDraft, contactId, createNote]);

  // Cmd / Ctrl + Enter posts the composer (parity with the Notes feed).
  useEffect(() => {
    const editor = composerRef.current?.getEditor();
    if (!editor) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        void handlePost();
      }
    };
    const dom = editor.view.dom;
    dom.addEventListener("keydown", onKeyDown);
    return () => dom.removeEventListener("keydown", onKeyDown);
  }, [handlePost]);

  const isLoading = notesLoading || (hasEmail && mailLoading);

  const subTabs: Array<{ key: ActivitySubTab; label: string; count?: number }> =
    [
      { key: "all", label: "All" },
      { key: "mail", label: "Mail", count: mailCount },
      { key: "notes", label: "Notes", count: notesCount },
    ];

  return (
    <div className="flex flex-col gap-4">
      {/* Combined AI summary + sentiment (mail + file notes) */}
      {hasEmail && (
        <section className="rounded-lg border border-border bg-secondary/20 p-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="flex items-center gap-1.5 font-sans text-xs font-semibold uppercase tracking-wider">
              <Sparkles className="size-3.5" />
              Summary &amp; sentiment
            </h3>
            <div className="flex items-center gap-2">
              {sentimentMeta && insight?.status === "complete" && (
                <Badge className={cn("border-0", sentimentMeta.className)}>
                  {sentimentMeta.label}
                </Badge>
              )}
              <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1 text-xs"
                disabled={generating || insight?.status === "pending"}
                onClick={() => void handleGenerate()}
              >
                <RefreshCw
                  className={cn(
                    "size-3",
                    (generating || insight?.status === "pending") &&
                      "animate-spin",
                  )}
                />
                {insight ? "Refresh" : "Generate"}
              </Button>
            </div>
          </div>
          {insight?.status === "pending" ? (
            <p className="mt-2 text-sm text-muted-foreground">
              Analysing the correspondence and notes…
            </p>
          ) : insight ? (
            <div className="mt-2 flex flex-col gap-1.5">
              <p className="whitespace-pre-wrap text-sm leading-relaxed">
                {insight.summary}
              </p>
              {insight.sentimentRationale && (
                <p className="text-xs text-muted-foreground">
                  {insight.sentimentRationale}
                </p>
              )}
              {stale && (
                <p className="text-xs text-amber-600">
                  New mail or notes have arrived since this summary was
                  generated.
                </p>
              )}
            </div>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">
              Generate an AI summary with a sentiment based on communications
              and notes.
            </p>
          )}
        </section>
      )}

      {/* Write to this contact. Opens the mailbox composer in the reader
          pane, which both sends directly and hands the draft to Morgan —
          the same control the mailbox uses, rather than a second one. */}
      {hasEmail && !readOnly && onOpenReader && (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1.5 text-xs"
            onClick={() =>
              onOpenReader({ kind: "compose", to: [emailAddress] })
            }
          >
            <Mail className="h-3 w-3" />
            New message
          </Button>
        </div>
      )}

      {/* All / Mail / Notes switcher */}
      <div className="grid grid-cols-3 gap-0.5 rounded-lg bg-muted p-0.5">
        {subTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setSubTab(tab.key)}
            className={cn(
              "relative rounded-md px-2 py-1.5 text-xs font-medium transition-colors",
              subTab === tab.key
                ? "bg-white text-foreground shadow-sm dark:bg-card"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className="ml-1.5 rounded-sm bg-muted px-1 text-[10px] tabular-nums text-muted-foreground">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ----------------------------- ALL ----------------------------- */}
      {subTab === "all" && (
        <div className="flex flex-col gap-4">
          {!readOnly && (
            <div className="flex flex-col gap-2">
              <NotesEditor
                ref={composerRef}
                value={composerDraft}
                onChange={setComposerDraft}
                placeholder="Add a file note about this contact — what was discussed, what to follow up on, anything worth remembering."
                minHeightClass="min-h-[80px]"
              />
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-muted-foreground">
                  Cmd/Ctrl + Enter to add
                </span>
                <Button
                  size="sm"
                  onClick={() => void handlePost()}
                  disabled={isPosting || isHtmlEmpty(composerDraft)}
                  className="gap-1.5"
                >
                  {isPosting ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Send className="h-3 w-3" />
                  )}
                  Add note
                </Button>
              </div>
            </div>
          )}

          {/* Filters — the feed carries four kinds of activity, and a
              practitioner chasing one of them should not have to read past
              the rest. */}
          {feedItems.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              {FEED_FILTERS.map((filter) => {
                const count = feedCounts[filter.key];
                const active = activeFilters.has(filter.key);
                if (count === 0 && !active) return null;
                return (
                  <button
                    key={filter.key}
                    type="button"
                    onClick={() => toggleFilter(filter.key)}
                    className={cn(
                      "rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors",
                      active
                        ? "border-primary/40 bg-primary/10 text-foreground"
                        : "border-border text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {filter.label}
                    <span className="ml-1 tabular-nums opacity-70">
                      {count}
                    </span>
                  </button>
                );
              })}
              {activeFilters.size > 0 && (
                <button
                  type="button"
                  onClick={() => setActiveFilters(new Set())}
                  className="px-1 text-[11px] text-muted-foreground underline-offset-2 hover:underline"
                >
                  Clear
                </button>
              )}
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-muted/10 p-6 text-center text-sm text-muted-foreground">
              {activeFilters.size > 0
                ? "Nothing matches the selected filters."
                : hasEmail
                  ? "No mail or notes for this contact yet."
                  : "No notes yet. Add an email address to see correspondence here too."}
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {visibleItems.map((item) =>
                item.kind === "note" ? (
                  <li key={`note-${item.note._id}`}>
                    <NoteRow
                      note={item.note}
                      canEdit={
                        !!currentUserId && item.note.createdBy === currentUserId
                      }
                      readOnly={readOnly}
                      onSave={async (content) => {
                        await updateNote({
                          noteId: item.note._id as Id<"jobContactNotes">,
                          content,
                        });
                      }}
                      onDelete={async () => {
                        await deleteNote({
                          noteId: item.note._id as Id<"jobContactNotes">,
                        });
                      }}
                    />
                  </li>
                ) : item.kind === "draft" ? (
                  <li key={`draft-${item.draft.draftId}`}>
                    <DraftFeedRow
                      draft={item.draft}
                      onOpen={
                        onOpenReader
                          ? () => onOpenReader(readerForDraft(item.draft))
                          : undefined
                      }
                      isActive={isDraftActive(activeReader, item.draft)}
                    />
                  </li>
                ) : (
                  <li key={`mail-${item.message._id}`}>
                    <MailFeedRow
                      message={item.message}
                      onOpenThread={onOpenThread}
                      isActive={activeThreadId === item.message.threadId}
                    />
                  </li>
                ),
              )}
            </ul>
          )}
        </div>
      )}

      {/* ----------------------------- MAIL ----------------------------- */}
      {subTab === "mail" && (
        <ContactCommsTab
          jobId={jobId}
          emailAddress={emailAddress}
          jobContactId={contactId}
          onOpenThread={onOpenThread}
          activeThreadId={activeThreadId}
          showInsight={false}
        />
      )}

      {/* ----------------------------- NOTES ---------------------------- */}
      {subTab === "notes" && (
        <ContactNotesFeed
          contactId={contactId}
          readOnly={readOnly}
          currentUserId={currentUserId}
          notesSummary={notesSummary}
          notesCount={notesCount}
          onOpenChat={onOpenChat}
          // The combined card above covers mail + notes; keep the notes-only
          // summary for contacts without an email address.
          showSummary={!hasEmail}
        />
      )}
    </div>
  );
}

/** Where a draft opens: its thread if it has one, else the draft itself. */
function readerForDraft(draft: DraftFeedItem): ContactReader {
  if (draft.source === "user") {
    return { kind: "userDraft", draftId: draft.draftId };
  }
  // A reply rides its thread, so the draft is read in context; a new
  // outbound has no thread and opens on its own.
  return draft.threadId
    ? { kind: "thread", threadId: draft.threadId }
    : { kind: "draft", draftId: draft.draftId };
}

/** True when the reader pane is already showing this draft. */
function isDraftActive(
  reader: ContactReader | undefined,
  draft: DraftFeedItem,
): boolean {
  if (!reader) return false;
  if (reader.kind === "draft" || reader.kind === "userDraft") {
    return reader.draftId === draft.draftId;
  }
  if (reader.kind === "thread") {
    return draft.threadId !== null && reader.threadId === draft.threadId;
  }
  return false;
}

/**
 * An unsent draft on the contact's feed.
 *
 * Shown with what it is waiting on: a Morgan draft needs approving before
 * it goes anywhere, the practitioner's own compose is simply unfinished.
 * The two are coloured apart — amber for something asking to be reviewed,
 * plain grey for a draft of one's own — so a queue of work to do never
 * hides among drafts that are nobody's business but the author's. Either
 * way it is correspondence that has not happened yet, so neither renders
 * as though it were sent.
 */
function DraftFeedRow({
  draft,
  onOpen,
  isActive,
}: {
  draft: DraftFeedItem;
  onOpen?: () => void;
  isActive: boolean;
}) {
  const needsReview = draft.source !== "user";
  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1.5 text-xs font-medium">
          <FileEdit
            className={cn(
              "size-3 shrink-0",
              needsReview ? "text-amber-600" : "text-muted-foreground",
            )}
          />
          <span className="truncate">{draft.subject || "(no subject)"}</span>
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          <Badge
            className={cn(
              "border-0 text-[10px]",
              needsReview
                ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                : "bg-muted text-muted-foreground",
            )}
          >
            {draft.revising
              ? "Morgan is revising"
              : needsReview
                ? "In review"
                : "Your draft"}
          </Badge>
          <span className="text-[11px] text-muted-foreground">
            {formatDistanceToNow(draft.createdAt, { addSuffix: true })}
          </span>
        </span>
      </div>
      <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
        {draft.bodyText}
      </p>
      {needsReview && draft.agentRationale && (
        <p className="mt-1.5 line-clamp-2 text-[11px] italic text-muted-foreground">
          {draft.agentRationale}
        </p>
      )}
    </>
  );

  const tone = needsReview
    ? "border-amber-300 bg-amber-50/40 dark:border-amber-900 dark:bg-amber-950/20"
    : "border-border bg-muted/20";

  if (!onOpen) {
    return (
      <div className={cn("rounded-md border border-dashed p-3", tone)}>
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "block w-full rounded-md border border-dashed p-3 text-left transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        tone,
        needsReview
          ? "hover:border-amber-400 hover:bg-amber-50/70"
          : "hover:border-primary/40 hover:bg-muted/40",
        isActive && "ring-1 ring-primary/40",
      )}
      title={
        needsReview
          ? "Open this draft to approve, edit, or reject it"
          : "Open this draft to finish and send it"
      }
    >
      {body}
    </button>
  );
}

/** One mail message row in the combined feed — mirrors the timeline row in
 *  `ContactCommsTab` so the two surfaces read identically. */
function MailFeedRow({
  message,
  onOpenThread,
  isActive,
}: {
  message: MailFeedMessage;
  onOpenThread?: (threadId: Id<"mailThreads">) => void;
  isActive: boolean;
}) {
  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs font-medium">
          {message.direction === "inbound" ? (
            <ArrowDownLeft className="size-3 text-emerald-600" />
          ) : (
            <ArrowUpRight className="size-3 text-blue-600" />
          )}
          {message.subject || "(no subject)"}
        </span>
        <span className="shrink-0 text-[11px] text-muted-foreground">
          {formatDistanceToNow(message.sentAt, { addSuffix: true })}
        </span>
      </div>
      <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
        {message.bodyText}
      </p>
    </>
  );
  if (onOpenThread) {
    return (
      <button
        type="button"
        onClick={() => onOpenThread(message.threadId)}
        className={cn(
          "block w-full rounded-md border border-border p-3 text-left transition-colors",
          "hover:border-primary/40 hover:bg-muted/40 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          message.direction === "outbound" && "bg-secondary/30",
          isActive && "border-primary/50 bg-primary/5",
        )}
        title="Open this thread"
      >
        {body}
      </button>
    );
  }
  return (
    <div
      className={cn(
        "rounded-md border border-border p-3",
        message.direction === "outbound" && "bg-secondary/30",
      )}
    >
      {body}
    </div>
  );
}
